import { useEffect, useState } from 'react'

import type { ChatMessage } from '@/types/domain'

/* The primitives every conversation surface shares (Story 8.3, FR-46, AD-22, 0051).
 *
 * This file WAS the case conversation's whole data layer — its own pair of
 * endpoints, its own poll, its own optimistic post. All of that moved to
 * `features/chat/api/useConversation`, which serves a case and a quote through
 * one set of hooks, because a case thread and a quote thread are the same
 * conversation with a different subject on it.
 *
 * What stayed is the part that was never about cases: the merge rule, the
 * visibility gate, the poll interval and the idempotency key. They are imported
 * by `useConversation` rather than copied into it, so a fix to the
 * reconciliation rule lands everywhere at once — and they stay HERE, at the
 * path that already had them, so the move reads in the diff as a deletion of
 * the duplicated half rather than as a rewrite of proven code.
 *
 * Nothing in this file talks to the network or to the query cache. Everything
 * that does now lives in `features/chat/api/useConversation`.
 */

/** How often a RECORD panel asks what has been said (AD-22).
 *
 *  Ten seconds is the AC's number, and the AC's 15-second delivery bound is what
 *  makes it a ceiling rather than a preference: one interval plus a request must
 *  fit inside fifteen seconds, so this cannot be relaxed to 20 without breaking
 *  the guarantee the story states.
 *
 *  The chat PAGE's list polls a minute instead (CONVERSATION_POLL_MS): it is a
 *  safety net behind the socket rather than the mechanism. A record panel is
 *  the surface the bound is stated about, so it keeps the ten. */
export const CHAT_POLL_MS = 10_000

/** Appends arrivals to a cached thread, dropping anything already held.
 *
 *  Exported for its test, and worth testing apart from any hook: it is the one
 *  piece of this surface that is pure logic rather than browser plumbing.
 *
 *  Deduplicated by id rather than appended blindly, because the same message
 *  genuinely can arrive twice — an optimistic post reconciles into the thread
 *  and the very next poll returns the server's copy of it, and two tabs share
 *  one cache. A thread that appended blindly would show a colleague's message
 *  twice and count it twice in the unread badge.
 *
 *  A message already held is REPLACED rather than skipped, so the poll's copy
 *  wins over an optimistic one holding the same id: the server's body is the
 *  trimmed one and its `sentAt` is the database clock's, not this browser's.
 *  The same replacement is what lets an edit and a tombstone land in place. */
export function mergeArrivals(
  held: ChatMessage[],
  arrivals: ChatMessage[],
): ChatMessage[] {
  if (arrivals.length === 0) return held

  const byId = new Map(held.map((message) => [message.id, message]))
  const fresh: ChatMessage[] = []

  for (const arrival of arrivals) {
    if (byId.has(arrival.id)) byId.set(arrival.id, arrival)
    else fresh.push(arrival)
  }

  const reconciled = held.map((message) => byId.get(message.id) ?? message)
  return fresh.length ? [...reconciled, ...fresh] : reconciled
}

/** Whether the document is currently being looked at.
 *
 *  A hook rather than a read of `document.hidden` at render time, because the
 *  value changes without React knowing: switching tabs fires an event and
 *  re-renders nothing. Subscribing is what lets the poll actually STOP — AD-22
 *  asks for a poll while visible, and a background tab that keeps asking every
 *  ten seconds is a request per record per tab per user, forever, for a panel
 *  nobody is looking at.
 *
 *  `useSyncExternalStore` would be the tidier shape, but it wants a stable
 *  snapshot function and `document.hidden` is read from a global that jsdom
 *  lets a test redefine mid-run; a subscribed state value re-reads on every
 *  event and is what makes the visibility test able to fail. */
export function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState(
    () => typeof document === 'undefined' || !document.hidden,
  )

  useEffect(() => {
    const onChange = () => setVisible(!document.hidden)
    document.addEventListener('visibilitychange', onChange)
    // Read once on mount too: the tab may already have been hidden when this
    // mounted, and waiting for the next event would poll until it fired.
    onChange()
    return () => document.removeEventListener('visibilitychange', onChange)
  }, [])

  return visible
}

/** A fresh idempotency key for one post attempt (UX-DR-24, AD-18).
 *
 *  Generated CLIENT-side and on the mutation's behalf rather than per request,
 *  which is the whole point: the same key rides every retry of one message, so
 *  a request that times out on a warehouse connection and is sent again comes
 *  back as the FIRST attempt's message rather than as a second identical
 *  bubble. A server-minted key could not do this — by the time the client has
 *  one, the ambiguous request has already happened.
 *
 *  `crypto.randomUUID` where it exists, with a fallback: it is unavailable on
 *  a page served over plain HTTP, which is exactly the LAN deployment somebody
 *  will eventually try, and a chat that threw on send there would be a very
 *  confusing bug to diagnose. */
export function newClientKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}
